import {
  autoUpdate,
  flip,
  offset,
  size,
  useDismiss,
  useFloating,
  useInteractions
} from '@floating-ui/react'
import { shift } from '@floating-ui/react-dom'
import { useScreen } from '@root/utils'
import { useCallback, useState, type RefObject } from 'react'

const MAX_HEIGHT_DROPDOWN = 300
const DEFAULT_MIN_WIDTH_DROPDOWN = 500

type Options = {
  value?: number[]
  onChange?: (tagIds: number[]) => void
  focusRef?: RefObject<HTMLElement | null>
  shouldRetainFocus?: () => boolean
  minWidth?: number
}

export const useTagSelectorFloating = ({
  value,
  onChange,
  focusRef,
  shouldRetainFocus,
  minWidth = DEFAULT_MIN_WIDTH_DROPDOWN
}: Options) => {
  const [isDropDownOpen, setIsDropDownOpen] = useState(false)
  const { isMobile } = useScreen()

  const handleOnSelect = useCallback(
    (tagId: number) => {
      const currentTagIds = value || []
      const isTagSelected = currentTagIds.includes(tagId)
      const newTagIds = isTagSelected
        ? currentTagIds.filter(id => id !== tagId)
        : [...currentTagIds, tagId]

      onChange?.(newTagIds)
    },
    [onChange, value]
  )

  const { context, refs } = useFloating({
    open: isDropDownOpen,
    onOpenChange: open => {
      setIsDropDownOpen(open)

      if (!open && !shouldRetainFocus?.() && focusRef?.current) {
        focusRef.current.focus()
      }
    },
    whileElementsMounted: autoUpdate,
    placement: isMobile ? 'bottom' : 'bottom-start',
    middleware: [
      offset(0),
      flip(),
      shift(),
      size({
        apply: ({ availableWidth, rects, elements }) => {
          Object.assign(elements.floating.style, {
            width: isMobile
              ? `${availableWidth}px`
              : `${Math.max(rects.reference.width, minWidth)}px`,
            maxHeight: `${MAX_HEIGHT_DROPDOWN}px`
          })
        }
      })
    ]
  })

  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  const toggleDropDown = () => {
    setIsDropDownOpen(prev => !prev)
  }

  return {
    isDropDownOpen,
    setIsDropDownOpen,
    toggleDropDown,
    handleOnSelect,
    context,
    refs,
    getReferenceProps,
    getFloatingProps
  }
}
